/**
 * Minimal fetch wrapper shared by all three apps:
 *  - attaches the access token,
 *  - transparently refreshes once on 401 (single in-flight refresh),
 *  - normalises API errors into ApiError,
 *  - supports multipart uploads.
 */

export interface ApiErrorBody { code: string; message: string; errors?: Record<string, string[]>; traceId?: string }

export class ApiError extends Error {
  constructor(public status: number, public body: ApiErrorBody) {
    super(body.message);
  }
  get code() { return this.body.code; }
  get fieldErrors() { return this.body.errors ?? {}; }
}

export interface Tokens { accessToken: string; accessTokenExpiresAtUtc: string; refreshToken: string }

// Apps served under sub-paths of one origin (/admin/, /partner/, /app/) each keep their own session.
const TOKENS_KEY = import.meta.env.BASE_URL === '/' ? 'gr.tokens' : `gr.tokens${import.meta.env.BASE_URL}`;

export const tokenStore = {
  get(): Tokens | null {
    try { const raw = localStorage.getItem(TOKENS_KEY); return raw ? (JSON.parse(raw) as Tokens) : null; } catch { return null; }
  },
  set(t: Tokens | null) {
    try { t ? localStorage.setItem(TOKENS_KEY, JSON.stringify(t)) : localStorage.removeItem(TOKENS_KEY); } catch { /* storage may be unavailable */ }
  },
};

export const apiBaseUrl: string = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

let refreshing: Promise<Tokens | null> | null = null;
let onUnauthorized: (() => void) | null = null;

/** Apps register a callback that clears auth state when refresh fails. */
export function setUnauthorizedHandler(fn: () => void) { onUnauthorized = fn; }

async function refreshTokens(): Promise<Tokens | null> {
  const current = tokenStore.get();
  if (!current?.refreshToken) return null;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: current.refreshToken }),
        });
        if (!res.ok) { tokenStore.set(null); return null; }
        const data = (await res.json()) as Tokens;
        tokenStore.set(data);
        return data;
      } catch {
        return null;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  auth?: boolean; // default true
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, form, query, signal, auth = true } = opts;
  const url = new URL(apiBaseUrl + path, window.location.origin);
  if (query) for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) url.searchParams.set(k, String(v));

  const doFetch = async (retry: boolean): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (!form) headers['Content-Type'] = 'application/json';
    if (auth) {
      const t = tokenStore.get();
      if (t?.accessToken) headers.Authorization = `Bearer ${t.accessToken}`;
    }
    const res = await fetch(url.toString(), { method, headers, body: form ?? (body !== undefined ? JSON.stringify(body) : undefined), signal });
    if (res.status === 401 && auth && retry) {
      const refreshed = await refreshTokens();
      if (refreshed) return doFetch(false);
      onUnauthorized?.();
    }
    return res;
  };

  const res = await doFetch(true);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const bodyErr: ApiErrorBody = (data && typeof data === 'object' && 'message' in data)
      ? (data as ApiErrorBody)
      : { code: `http_${res.status}`, message: res.statusText || 'Request failed' };
    throw new ApiError(res.status, bodyErr);
  }
  return data as T;
}

function safeJson(text: string): unknown { try { return JSON.parse(text); } catch { return text; } }
